/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { DASHBOARD_DATA_TYPES } from 'constants';

import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { Checkbox, MultiSelect } from 'carbon-addons-iot-react';

import { DataItemsPropTypes } from '../../../../Setup/constants/DataPropTypes';

const messages = defineMessages({
  overlayWithAlerts: 'Overlay with alerts',
  alertCardContentTooltip:
    'Show or hide columns. You can show alert fields and dimensions as columns',
  alerts: 'Alerts',
  selectAnAlert: 'Select an alert',
  required: 'Required',
  'close.menu': 'Close menu',
  'open.menu': 'Open menu',
  'clear.all': 'Clear all',
  'clear.selection': 'Clear selection',
});

const propTypes = {
  cardConfig: PropTypes.shape({
    dataSource: PropTypes.shape({
      additionalData: PropTypes.shape({
        type: PropTypes.string,
        dataFilter: PropTypes.shape({
          name: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        }),
      }),
    }),
    // eslint-disable-next-line react/forbid-prop-types
    content: PropTypes.object,
    id: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  alertDataItems: DataItemsPropTypes,
};

const defaultProps = {
  cardConfig: undefined,
  alertDataItems: [],
};

const TimeSeriesCardEditContent = ({ cardConfig, onChange, alertDataItems }) => {
  const { formatMessage, locale } = useIntl();
  // TODO workaround issue with default compare items function that uses in memory compare
  const alertsSelectItems = alertDataItems.map(({ name }) => ({ label: name, id: name }));
  return (
    <div style={{ display: 'flex', flexFlow: 'column' }}>
      <Checkbox
        id="alert-overlay-checkbox"
        labelText={formatMessage(messages.overlayWithAlerts)}
        checked={Boolean(
          cardConfig?.dataSource?.additionalData?.type === DASHBOARD_DATA_TYPES.ALERT
        )}
        onChange={value => {
          if (!value) {
            // if they disable the alert overlay, we need to remove the thresholds and additionalData
            onChange({
              ...cardConfig,
              dataSource: omit(cardConfig?.dataSource, 'additionalData'),
            });
          } else {
            onChange({
              ...cardConfig,
              dataSource: {
                ...cardConfig.dataSource,
                additionalData: { type: DASHBOARD_DATA_TYPES.ALERT },
              },
            });
          }
        }}
      />
      {
        // only show the data item selector if they're marking up alerts
        cardConfig?.dataSource?.additionalData?.type === DASHBOARD_DATA_TYPES.ALERT ? (
          <MultiSelect
            id="alert-selector-multiselect"
            name="alert-selector-multiselect"
            key={cardConfig?.id}
            locale={locale}
            label={formatMessage(messages.selectAnAlert)}
            items={alertsSelectItems}
            translateWithId={idToTranslate => formatMessage(messages[idToTranslate])}
            onChange={e => {
              const selectedAlerts = e?.selectedItems;
              // and pass up the event in case the container components needs to react to this event in some way
              onChange({
                ...cardConfig,
                dataSource: {
                  ...cardConfig?.dataSource,
                  additionalData: {
                    ...omit(
                      cardConfig?.dataSource?.additionalData,
                      isEmpty(selectedAlerts) ? 'dataFilter' : null // if we're back to the initial selection then remove
                    ),
                    // if no alrets are selected then remove
                    ...(!isEmpty(selectedAlerts)
                      ? {
                          dataFilter: {
                            name: selectedAlerts.map(selectedAlert => selectedAlert.id),
                          },
                        }
                      : {}), // otherwise set
                  },
                },
              });
            }}
            initialSelectedItems={alertsSelectItems.filter(alert =>
              cardConfig?.dataSource?.additionalData?.dataFilter?.name.includes(alert.id)
            )}
            invalid={!cardConfig?.dataSource?.additionalData?.dataFilter?.name}
            invalidText={formatMessage(messages.required)}
            // eslint-disable-next-line react/jsx-props-no-spreading
          />
        ) : null
      }
    </div>
  );
};

TimeSeriesCardEditContent.propTypes = propTypes;
TimeSeriesCardEditContent.defaultProps = defaultProps;
export default TimeSeriesCardEditContent;
